import { getApiKeyForEnvironment } from './ApiKeyManager';
import { LLMClient } from './LLMClient';
import type { Model, SystemMessage, UserMessage } from './types';
import { MessageRole } from './types';

import Mustache from 'mustache';

/**
 * Renders a Mustache prompt template with the given context.
 * Templates are shared with the Python eval system via .mustache files
 * in openai_gpt/resources/.
 */
export function renderPromptTemplate(template: string, context: Record<string, unknown>): string {
    return Mustache.render(template, context);
}

/**
 * Builds a [system, user] message pair from Mustache prompt template strings.
 * Templates should be imported from the shared prompt files via:
 *   import systemTpl from 'prompts/<domain>/system.mustache';
 */
export function buildPromptMessages(
    systemTemplate: string,
    userTemplate: string,
    context: Record<string, unknown>,
): [SystemMessage, UserMessage] {
    return [
        {
            role: MessageRole.SYSTEM,
            content: renderPromptTemplate(systemTemplate, context),
        },
        {
            role: MessageRole.USER,
            content: renderPromptTemplate(userTemplate, context),
        },
    ];
}

interface PromptOperationConfig<TResult> {
    systemTemplate: string;
    userTemplate: string;
    defaultModel: Model;
    task: string;
    parse: (content: string) => TResult;
    fallback?: TResult;
}

export function createPromptOperation<TParams extends Record<string, unknown>, TResult>(
    config: PromptOperationConfig<TResult>,
): (params: TParams, model?: Model) => Promise<TResult> {
    return async (params, model) => {
        try {
            const client = new LLMClient(getApiKeyForEnvironment(), model ?? config.defaultModel);
            const messages = buildPromptMessages(
                config.systemTemplate,
                config.userTemplate,
                params,
            );
            const response = await client.complete(messages, undefined, undefined, config.task);
            return config.parse(response.content);
        } catch {
            if (config.fallback !== undefined) {
                return config.fallback;
            }
            throw new Error(
                `Prompt operation failed and no fallback was provided. ` +
                    `Model: ${model ?? config.defaultModel}`,
            );
        }
    };
}
